import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { apiClient, fromAxiosError } from '@/core/api';
import type { ApiResponse } from '@/core/api/types';
import { useAuth, normalizeUserType } from '@/core/auth';

interface UserTypeRecord {
  userId: number;
  userType: string;
}

/**
 * Order of preference when an account still carries more than one role.
 *
 * Only reached by accounts that predate one-role-per-person; the administrator
 * row wins because it is the one that can see and fix the others.
 */
const PREFERRED = ['ADMIN', 'CUSTOMER', 'TOWPROVIDER', 'ADJUSTER', 'DRIVER'];

/**
 * Finishes signing in when the token has not settled on a role yet.
 *
 * There used to be a screen here: a login could return a token with no menu
 * resolved, and the user picked which of their roles to be. It was asked of
 * everybody who had more than one row, including the administrator, on every
 * single sign-in — a question with an obvious answer, in the way of the thing
 * they came to do.
 *
 * Roles are one-per-account now, so for almost everyone this state is not
 * reachable at all. Where it is — an account created before that was true — the
 * choice is made here instead of being handed to the user, and the
 * administrator role wins because it is the one that can go and fix the
 * duplicate.
 *
 * Kept as a real step rather than deleted outright because the token genuinely
 * has no menu until this resolves. Skipping it would drop somebody into an
 * application with no navigation and no explanation.
 */
export default function ResolveRole() {
  const { t } = useTranslation();
  const { selectRole, logout } = useAuth();
  const [error, setError] = useState<string | null>(null);
  // Strict mode mounts effects twice in development; picking a role is not
  // something to do twice.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    let cancelled = false;

    void (async () => {
      try {
        const res = await apiClient.get<ApiResponse<UserTypeRecord[]>>(
          '/api/v1/Users/GetUserTypes',
        );
        const roles = res.data.data ?? [];
        if (cancelled) return;

        if (roles.length === 0) {
          setError(t('nav.account.noRoles'));
          return;
        }

        const best =
          PREFERRED.map((wanted) =>
            roles.find((r) => normalizeUserType(r.userType) === wanted),
          ).find((r) => r !== undefined) ?? roles[0];

        await selectRole(best.userId);
        // `currentUser` updates in place; HomeRouter re-renders and dispatches.
      } catch (err) {
        if (!cancelled) setError(fromAxiosError(err).message);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectRole, t]);

  if (error !== null) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '60vh', p: 3, gap: 2 }}>
        <Alert severity="error">{error}</Alert>
        <Button onClick={() => void logout()}>{t('nav.logout')}</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '60vh' }}>
      <CircularProgress aria-label={t('common.loading')} />
    </Box>
  );
}
